import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";

const PRIORITY = ["low", "medium", "high"];
const COLS = [["open", "Open"], ["in_progress", "In Progress"], ["done", "Done"]];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee: "", due_date: "", priority: "medium", task_type: "general" });
  const { canEdit } = useAuth();

  const load = useCallback(() => api.get("/tasks").then((r) => setTasks(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try { await api.post("/tasks", form); setOpen(false); setForm({ title: "", description: "", assignee: "", due_date: "", priority: "medium", task_type: "general" }); load(); toast.success("Task created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const move = async (id, status) => { await api.patch(`/tasks/${id}`, { status }); load(); };
  const remove = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  const overdue = (t) => t.due_date && t.status !== "done" && new Date(t.due_date) < new Date();

  const grouped = useMemo(() => {
    const map = { open: [], in_progress: [], done: [] };
    tasks.forEach((t) => { (map[t.status] || (map[t.status] = [])).push(t); });
    return map;
  }, [tasks]);

  return (
    <div>
      <PageHeader title="Tasks & Reminders" subtitle="Evidence requests, reviews, and follow-ups"
        action={canEdit && <Button data-testid="new-task-btn" onClick={() => setOpen(true)} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Task</Button>} />
      <div className="p-8">
        {tasks.length === 0 ? <Empty title="No tasks yet" hint="Create follow-up tasks for evidence and reviews." /> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            {COLS.map(([status, label]) => (
              <div key={status} className="bg-card min-h-[300px]" data-testid={`task-col-${status}`}>
                <div className="border-b border-border px-4 py-3 font-display font-bold flex justify-between bg-secondary/40">{label} <span className="font-mono text-sm text-muted-foreground">{grouped[status].length}</span></div>
                <div className="p-3 space-y-3">
                  {grouped[status].map((t) => (
                    <div key={t.id} data-testid={`task-${t.id}`} className={`border p-3 ${overdue(t) ? "border-red-400 bg-red-50" : "border-border"}`}>
                      <div className="flex justify-between items-start"><div className="font-medium text-sm">{t.title}</div>{canEdit && <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>}</div>
                      {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                      <div className="flex items-center gap-2 mt-2 text-[11px]">
                        <span className={`border px-1.5 py-0.5 uppercase font-bold ${t.priority === "high" ? "border-red-300 text-red-700 bg-red-50" : "border-border text-muted-foreground"}`}>{t.priority}</span>
                        {t.due_date && <span className={`font-mono ${overdue(t) ? "text-red-600 font-bold" : "text-muted-foreground"}`}>{t.due_date.slice(0, 10)}</span>}
                        {t.assignee && <span className="text-muted-foreground">· {t.assignee}</span>}
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 mt-2">
                          {COLS.filter(([s]) => s !== status).map(([s, l]) => (
                            <button key={s} data-testid={`move-${t.id}-${s}`} onClick={() => move(t.id, s)} className="border border-border px-2 py-0.5 text-[11px] hover:bg-primary hover:text-white">→ {l}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assignee</Label><Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-none mt-1 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="create-task-btn" onClick={create} className="rounded-none w-full bg-primary hover:bg-primary/90">Create Task</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
